/**
 * A guarda das imagens (`SC-07`, ADR-089): a regra de cada concessão e o que o comando do dono do
 * banco já fez.
 *
 * Toda foto de pórtico fica 90 dias da passagem (e da chegada, quando chegou depois); depois, a
 * da passagem que terminou bem sai, e a da que deu problema fica, travada, pelo prazo do regime. **Quem apaga é o comando diário da VM,
 * nunca a tela**: aqui só se lê a regra (do regime em vigor de cada concessão, com a fonte e o
 * que falta confirmar), a última passada, o que espera o armazenamento e o aviso de quando o
 * agendador parece parado — tudo decidido no servidor.
 */
import { useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  GuardaDaConcessao,
  GuardaDasImagensNaTela,
  PassadaNaTela,
} from "../api/tipos";
import { Badge, Cartao, Estado } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { dataEHora, numeroLongo } from "../formato";

const CAMINHO = "/tollvision/guarda";

export function GuardaDasImagens() {
  const [guarda, definirGuarda] = useState<GuardaDasImagensNaTela | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api<GuardaDasImagensNaTela>(CAMINHO)
      .then((lida) => vivo && definirGuarda(lida))
      .catch(
        (falha: unknown) =>
          vivo &&
          definirErro(
            falha instanceof ErroDaApi
              ? falha.message
              : "Não foi possível ler a guarda das imagens.",
          ),
      );
    return () => {
      vivo = false;
    };
  }, []);

  if (erro) return <Estado tipo="erro" titulo={erro} />;
  if (!guarda)
    return <Estado tipo="carregando" titulo="Lendo a guarda das imagens…" />;

  return (
    <div className="grid gap-3">
      <p className="max-w-prose text-[11.5px] text-tinta-3">
        Toda foto de pórtico fica no mínimo <b>90 dias da passagem</b> (Res.
        CONTRAN nº 1.013/2024, art. 5º, § 7º) — e da chegada ao cockpit, quando
        chegou depois. Passado isso, a da passagem que <b>terminou bem</b> —
        paga no prazo do usuário ou isenta — sai, e a da que <b>deu problema</b>{" "}
        — evasão, fraude, perda, cancelamento, ou nenhum desfecho em 120 dias —
        fica, travada no armazenamento, pelo prazo do regime. Quem apaga é o
        comando diário do dono do banco, na máquina do cockpit: nada sai por
        esta tela.
      </p>
      {guarda.parada && (
        <p
          role="alert"
          className="rounded-cartao border border-amarelo-borda bg-amarelo-fundo px-2.5 py-1.5 text-[11.5px] text-amarelo"
        >
          {guarda.ultima_passada
            ? `Há ${guarda.dias_sem_passada} dias ou mais sem passada da guarda: o agendador da máquina parou?`
            : "Nenhuma passada da guarda ainda, e já há foto passando dos 90 dias: o agendador da máquina está ligado?"}
        </p>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {guarda.concessoes.map((c) => (
          <DaConcessao key={c.concessao_id} concessao={c} />
        ))}
      </div>
      <Passadas guarda={guarda} />
    </div>
  );
}

function DaConcessao({ concessao }: { concessao: GuardaDaConcessao }) {
  return (
    <Cartao
      titulo={`${concessao.concessao_nome} · ${concessao.concessao_codigo}`}
      icone={<Icone.Escudo />}
    >
      <dl
        aria-label={`A guarda de ${concessao.concessao_codigo}`}
        className="grid grid-cols-1 gap-x-3 gap-y-1 text-[11.5px] md:grid-cols-[auto_1fr]"
      >
        <dt className="text-tinta-3">Regime</dt>
        <dd className="text-tinta">{concessao.regime_nome}</dd>
        <dt className="text-tinta-3">Toda foto</dt>
        <dd className="text-tinta">{concessao.minima_dias} dias da passagem</dd>
        <dt className="text-tinta-3">A que terminou bem</dt>
        <dd>
          {concessao.apaga_a_que_terminou_bem ? (
            <Badge cor="azul">
              sai depois dos {concessao.minima_dias} dias
            </Badge>
          ) : (
            <Badge cor="amarelo">fica até a confirmação</Badge>
          )}
        </dd>
        <dt className="text-tinta-3">A que deu problema</dt>
        <dd className="text-tinta">fica {concessao.problema_por}, travada</dd>
      </dl>
      <p className="text-[10.5px] text-tinta-3">Fonte: {concessao.fonte}.</p>
      {concessao.pendente && (
        <p role="note" className="text-[11px] text-amarelo">
          {concessao.pendente}
        </p>
      )}
    </Cartao>
  );
}

function Passadas({ guarda }: { guarda: GuardaDasImagensNaTela }) {
  const ultima = guarda.ultima_passada;
  // No disco, que não trava, a guardada nunca sai da conta "para travar": não é espera.
  const trava = ultima?.trava ?? true;
  const aTravar = trava ? guarda.a_travar_no_armazenamento : 0;
  return (
    <Cartao titulo="As passadas do comando" icone={<Icone.Lista />}>
      {ultima ? (
        <UltimaPassada passada={ultima} />
      ) : (
        <p className="text-[11.5px] text-tinta-3">
          Nenhuma passada ainda. O comando roda todo dia pelo agendador da
          máquina; sem ele, nenhuma foto sai.
        </p>
      )}
      {guarda.passadas > 0 && (
        <p className="text-[11.5px] text-tinta-2">
          Desde a primeira, em {numeroLongo(guarda.passadas)}{" "}
          {guarda.passadas === 1 ? "passada" : "passadas"}:{" "}
          {numeroLongo(guarda.apagadas)}{" "}
          {guarda.apagadas === 1
            ? "passagem julgada para sair"
            : "passagens julgadas para sair"}{" "}
          ({numeroLongo(guarda.imagens_apagadas)}{" "}
          {guarda.imagens_apagadas === 1 ? "foto apagada" : "fotos apagadas"} no
          armazenamento), {numeroLongo(guarda.guardadas)}{" "}
          {guarda.guardadas === 1 ? "guardada" : "guardadas"} e{" "}
          {numeroLongo(guarda.mantidas)}{" "}
          {guarda.mantidas === 1 ? "mantida" : "mantidas"}.
        </p>
      )}
      {(guarda.a_apagar_no_armazenamento > 0 || aTravar > 0) && (
        <p className="text-[11.5px] text-amarelo">
          Esperando o armazenamento:{" "}
          {numeroLongo(guarda.a_apagar_no_armazenamento)} para apagar
          {trava ? ` e ${numeroLongo(aTravar)} para travar` : ""} — a passada
          seguinte tenta de novo.
        </p>
      )}
    </Cartao>
  );
}

function UltimaPassada({ passada }: { passada: PassadaNaTela }) {
  if (passada.problema) {
    return (
      <div className="grid gap-1 text-[11.5px]">
        <p className="text-tinta">
          Última passada: <b>{dataEHora(passada.em)}</b>.
        </p>
        <p className="text-vermelho">{passada.problema}</p>
      </div>
    );
  }
  return (
    <div className="grid gap-1 text-[11.5px]">
      <p className="text-tinta">
        Última passada: <b>{dataEHora(passada.em)}</b> —{" "}
        {numeroLongo(passada.julgadas)} passagens olhadas:{" "}
        {numeroLongo(passada.apagadas)} julgadas para sair,{" "}
        {numeroLongo(passada.guardadas)} guardadas,{" "}
        {numeroLongo(passada.mantidas)} mantidas e{" "}
        {numeroLongo(passada.esperando)} esperando.
      </p>
      <p className="text-tinta-2">
        {numeroLongo(passada.imagens_apagadas)} fotos apagadas e{" "}
        {numeroLongo(passada.imagens_travadas)} travadas no armazenamento.
        {passada.imagens_faltando > 0 &&
          ` ${numeroLongo(passada.imagens_faltando)} ${passada.imagens_faltando === 1 ? "já não estava lá" : "já não estavam lá"}.`}
        {!passada.trava &&
          " O armazenamento desta instalação é o disco, que não trava: a foto guardada fica protegida só pelo banco."}
      </p>
      <p className="text-tinta-3">
        Julgadas todas as fotos chegadas até {dataEHora(passada.julgado_ate)}.
      </p>
      {passada.recusadas > 0 && (
        <p className="text-vermelho">
          {numeroLongo(passada.recusadas)}{" "}
          {passada.recusadas === 1
            ? "decisão o banco recusou"
            : "decisões o banco recusou"}{" "}
          — é erro a investigar: a passagem fica sem decisão até ele ser
          corrigido.
        </p>
      )}
      {passada.falhas > 0 && (
        <p className="text-vermelho">
          {numeroLongo(passada.falhas)}{" "}
          {passada.falhas === 1
            ? "foto o armazenamento recusou"
            : "fotos o armazenamento recusou"}{" "}
          — a passada seguinte tenta de novo.
        </p>
      )}
    </div>
  );
}
